export type AggregatorOption = string; // Dynamic gateway names from API

export interface NetbankingInfo {
  bankId: string;
  hybridBankId?: string;
  lumpsumDebitCardBankId?: string;
  lumpsum: boolean;
  hybrid: boolean;
  lumpsumDebitCard: boolean;
}

export interface EmandateInfo {
  bankId: string;
  netBanking: boolean;
  debitCard: boolean;
  aadhaar: boolean;
  pan: boolean;
}

export interface BlacklistEntry {
  id?: number | string;
  merchantId: string;
  gateway: string;
  bankName: string;
  ifscCode: string;
  paymode?: string;
  trtype?: string;
  isActive: boolean;
  remarks?: string;
  reason?: string;
  performedBy?: string;
  updatedAt?: string;
}

export interface BankRow {
  id: number;
  name: string;
  ifscCode: string;
  netbanking: NetbankingInfo;
  emandate: EmandateInfo;
  blacklistedMerchants?: BlacklistEntry[];
  blacklistReason?: string;
}

export interface BankFormData {
  gateway: string;
  name: string;
  ifscCode: string;
  nbBankId: string;
  nbHybridBankId: string;
  nbDebitCardBankId: string;
  nbLumpsum: boolean;
  nbHybrid: boolean;
  nbDebitCard: boolean;
  emdBankId: string;
  emdNetBanking: boolean;
  emdDebitCard: boolean;
  emdAadhaar: boolean;
  emdPan: boolean;
  remarks: string;
}

export const getGatewayParam = (option: AggregatorOption): string => {
  // For dynamic gateways, use the gateway ID directly
  // The API should provide the correct ID that can be used in requests
  // Fallback to original mapping for backward compatibility
  const fallbackMapping: Record<string, string> = {
    'Billdesk V1': 'Billdesk1',
    'Billdesk V2': 'Billdesk2',
    'CAMS Pay': 'CamsPay',
    'Kotak': 'Kotak',
    'CashFree': 'CashFree',
  };
  
  return option || fallbackMapping[option] || option;
};

export const coerceBoolean = (value: any): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') return ['true', 'yes', 'y', '1'].includes(value.toLowerCase());
  return !!value;
};

export const mapToBankRow = (item: any, index: number): BankRow => ({
  id: item.id ?? index + 1,
  name: item.name ?? item.bankName ?? '-',
  ifscCode: item.ifscCode ?? item.ifsc ?? '-',
  netbanking: {
    bankId: item.lumpsumNetBankingBankId ?? '-',
    hybridBankId: item.hybridNetBankingBankId ?? '-',
    lumpsumDebitCardBankId: item.lumpsumDebitCardBankId ?? '-',
    lumpsum: coerceBoolean(
      item.netbanking?.lumpsum ??
      item.nbLumpsum ??
      item.LumpsumNetBanking ??
      item.LumpsumNetbanking
    ),
    hybrid: coerceBoolean(
      item.netbanking?.hybrid ??
      item.nbHybrid ??
      item.HybridNetBanking ??
      item.HybridNetbanking
    ),
    lumpsumDebitCard: coerceBoolean(
      item.netbanking?.lumpsumDebitCard ??
      item.LumpsumDebitCard
    ),
  },
  emandate: {
    bankId: item.emandateBankId ?? '-',
    netBanking: coerceBoolean(
      item.emandate?.netBanking ??
      item.emdNetBanking ??
      item.EmandateNetBanking ??
      item.EmandateNetbanking
    ),
    debitCard: coerceBoolean(
      item.emandate?.debitCard ?? item.emdDebitCard ?? item.EmandateDebitCard
    ),
    aadhaar: coerceBoolean(
      item.emandate?.aadhaar ?? item.emdAadhaar ?? item.EmandateAadhar
    ),
    pan: coerceBoolean(
      item.emandate?.pan ?? item.emdPan ?? item.EmandatePAN ?? item.EmandatePan
    ),
  },
  blacklistedMerchants: (item.blacklistedMerchants || []).map((m: any) => 
    typeof m === 'string' 
      ? { merchantId: m, isActive: true } 
      : { ...m, isActive: m.isActive !== undefined ? coerceBoolean(m.isActive) : true }
  ),
  blacklistReason: item.blacklistReason || '',
});
